import { Component, For, Show } from 'solid-js';
import { getToastsSignal, removeToast } from '../../stores/toastStore';
import { TbCheck, TbX, TbAlertCircle, TbInfoCircle } from 'solid-icons/tb';

/**
 * Toast Component
 * Displays notifications in the top-right corner with smooth animations
 * Supports structured layout with title, subtitle, message, and action buttons
 */
const Toast: Component = () => {
  const toasts = getToastsSignal();

  const getIconAndColor = (type: string) => {
    switch (type) {
      case 'success':
        return {
          icon: TbCheck,
          bg: 'bg-gray-900 dark:bg-gray-800',
          border: 'border-l-4 border-emerald-500',
          text: 'text-gray-100 dark:text-gray-100',
          icon_color: 'text-emerald-500',
          accent_text: 'text-emerald-400',
          button_hover: 'hover:bg-gray-800 dark:hover:bg-gray-700',
          subtitle: 'text-gray-400 dark:text-gray-500',
        };
      case 'error':
        return {
          icon: TbX,
          bg: 'bg-gray-900 dark:bg-gray-800',
          border: 'border-l-4 border-red-500',
          text: 'text-gray-100 dark:text-gray-100',
          icon_color: 'text-red-500',
          accent_text: 'text-red-400',
          button_hover: 'hover:bg-gray-800 dark:hover:bg-gray-700',
          subtitle: 'text-gray-400 dark:text-gray-500',
        };
      case 'warning':
        return {
          icon: TbAlertCircle,
          bg: 'bg-gray-900 dark:bg-gray-800',
          border: 'border-l-4 border-amber-500',
          text: 'text-gray-100 dark:text-gray-100',
          icon_color: 'text-amber-500',
          accent_text: 'text-amber-400',
          button_hover: 'hover:bg-gray-800 dark:hover:bg-gray-700',
          subtitle: 'text-gray-400 dark:text-gray-500',
        };
      default:
        return {
          icon: TbInfoCircle,
          bg: 'bg-gray-900 dark:bg-gray-800',
          border: 'border-l-4 border-blue-500',
          text: 'text-gray-100 dark:text-gray-100',
          icon_color: 'text-blue-500',
          accent_text: 'text-blue-400',
          button_hover: 'hover:bg-gray-800 dark:hover:bg-gray-700',
          subtitle: 'text-gray-400 dark:text-gray-500',
        };
    }
  };

  return (
    <div class="fixed top-6 right-6 z-50 space-y-3 pointer-events-none max-w-sm">
      <For each={toasts()}>
        {(toast) => {
          const { icon: Icon, bg, border, text, icon_color, button_hover, subtitle } = getIconAndColor(toast.type);
          const hasActions = toast.actions && toast.actions.length > 0;

          return (
            <div
              class={`${bg} ${border} rounded-lg shadow-lg animate-slideIn pointer-events-auto transition-all duration-200 overflow-hidden`}
            >
              {/* Header with Icon and Title */}
              <div class="relative p-4 pb-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeToast(toast.id);
                  }}
                  class={`absolute top-3 right-3 flex-shrink-0 hover:opacity-70 transition-opacity ${text}`}
                  title="Dismiss"
                  aria-label="Dismiss notification"
                >
                  <TbX size={18} />
                </button>

                {/* Title and Icon */}
                <Show when={toast.title}>
                  <div class="flex items-center gap-3 mb-1">
                    <Icon size={20} class={icon_color} />
                    <h3 class={`text-sm font-semibold ${text}`}>{toast.title}</h3>
                  </div>
                </Show>

                {/* Subtitle */}
                <Show when={toast.subtitle}>
                  <p class={`text-xs ${subtitle} ml-8`}>{toast.subtitle}</p>
                </Show>
              </div>

              {/* Message Content */}
              <div class="px-4 pb-3 pt-0">
                <p class={`text-sm ${text} leading-relaxed`}>{toast.message}</p>
              </div>

              {/* Action Buttons */}
              <Show when={hasActions}>
                <div class="flex gap-2 px-4 py-3 bg-gray-800/50 dark:bg-gray-700/30 border-t border-gray-700 dark:border-gray-600">
                  <For each={toast.actions}>
                    {(action) => (
                      <button
                        onClick={() => {
                          action.onClick();
                          removeToast(toast.id);
                        }}
                        class={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${text} ${button_hover} bg-gray-700/50 dark:bg-gray-600/50`}
                      >
                        {action.label}
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          );
        }}
      </For>
    </div>
  );
};

export default Toast;
